import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// =====================
// Toast Configuration
// =====================
const toastConfig = {
  position: "top-right",
  autoClose: 5000, // 5 seconds
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
};

// =====================
// Toast Helpers
// =====================
const currentToastId = "api-toast-latest"; // stable ID

const showToast = (message, type = "default") => {
  const toastOptions = {
    ...toastConfig,
    toastId: currentToastId,
  };

  if (toast.isActive(currentToastId)) {
    // Update existing toast instead of closing/reopening
    toast.update(currentToastId, {
      render: message,
      type,
      autoClose: 5000,
      isLoading: false,
    });
  } else {
    // Show a new toast
    switch (type) {
      case "success":
        toast.success(message, toastOptions);
        break;
      case "error":
        toast.error(message, toastOptions);
        break;
      default:
        toast(message, toastOptions);
    }
  }
};

// =====================
// Helper: Get Auth Token
// =====================
const getAuthToken = () => Cookies.get("authToken");

// =====================
// Axios Instance
// =====================
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// =====================
// Endpoints that do NOT require auth
// =====================
const excludedEndpoints = [
  "Authentication/login",
  "Common/GetStates",
  "Common/GetCities",
];

// Special endpoints that should show toasts
const specialEndpoints = {
  login: "Authentication/login",
  logout: "Authentication/logout",
};

// =====================
// APIs that should NOT show toasters
// =====================
const noToastEndpoints = [
  "LetterGradeRemarkCalc/GetBatchesByCourse",
  "LetterGradeRemarkCalc/GetCoursesByInstitute"
];

// =====================
// Helper: Redirect to Login
// =====================
const redirectToLogin = () => {
  // Clear all auth-related cookies
  clearAuthCookies();
  
  // Show session expired message
  showToast("Your session has expired. Please login again.", "error");
  
  // Redirect to login page after a short delay
  setTimeout(() => {
    // Check if we're already on the login page to avoid unnecessary redirects
    if (window.location.pathname !== "/EMS") {
      window.location.href = "/EMS";
    }
  }, 1000);
};

// =====================
// Request Interceptor
// =====================
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    const isExcluded = excludedEndpoints.some((endpoint) =>
      config.url.includes(endpoint)
    );

    if (!isExcluded && token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    
    // Don't set Content-Type for FormData (browser will set it automatically with boundary)
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// =====================
// Helper: Clear Auth Cookies
// =====================
const clearAuthCookies = () => {
  ["authToken", "u_id", "from", "hasSeenPopup", "i_id", "r_id", "i_nm"].forEach(
    (cookie) => Cookies.remove(cookie)
  );
};

// =====================
// Response Interceptor
// =====================
apiClient.interceptors.response.use(
  (response) => {
    // Check if this is a login or logout response
    const isLogin = response.config.url.includes(specialEndpoints.login);
    const isLogout = response.config.url.includes(specialEndpoints.logout);

    // Show success message only for login/logout
    if ((isLogin || isLogout) && response.data?.message) {
      showToast(response.data.message, "success");
    }

    return response;
  },
  (error) => {
    const status = error.response?.status || null;
    const url = error.config?.url || "";
    const isLogin = url.includes(specialEndpoints.login);
    const isLogout = url.includes(specialEndpoints.logout);
    const isNoToastEndpoint = noToastEndpoints.some(endpoint => 
      url.includes(endpoint)
    );
    const isNetworkError =
      !error.response ||
      error.code === "ERR_NETWORK" ||
      error.message.includes("net::ERR_HTTP2_PROTOCOL_ERROR");

    // Handle 401 Unauthorized - Logout and redirect to login
    if (status === 401) {
      console.warn("401 Unauthorized - Logging out user:", url);
      redirectToLogin();
      
      // Don't show additional toast, redirectToLogin already shows one
      return Promise.reject(error);
    }

    // Handle network errors (but not for excluded endpoints)
    if (isNetworkError && !isLogin && !isLogout) {
      clearAuthCookies();
      
      if (!isNoToastEndpoint) {
        showToast("Network error - please check your connection", "error");
      }
      
      // Redirect to login if not already there
      if (window.location.pathname !== "/") {
        setTimeout(() => {
          window.location.href = "/";
        }, 1500);
      }
    }

    // Get error message from response or default
    const errorMessage =
      error.response?.data?.message ||
      (isNetworkError
        ? "Network error - please check your connection"
        : `Request failed with status ${status}` || "An unexpected error occurred");

    // Show error toast for login/logout failures or 409 conflicts, but skip no-toast endpoints
    if ((isLogin || isLogout || status === 409) && !isNoToastEndpoint) {
      showToast(errorMessage, "error");
    }

    return Promise.reject(error);
  }
);

// =====================
// Helper: Check if endpoint should show toast
// =====================
const shouldShowToast = (endpoint) => {
  return !noToastEndpoints.some(noToastEndpoint => 
    endpoint.includes(noToastEndpoint)
  );
};

// =====================
// API Methods
// =====================
export const getData = async (endpoint, responseType = "json") => {
  try {
    const response = await apiClient.get(endpoint, { responseType });
    return responseType === "blob" ? response : response.data;
  } catch (error) {
    // Don't show error toast for 401 (handled in interceptor)
    if (error.response?.status !== 401 && shouldShowToast(endpoint)) {
      const errorMessage = error.response?.data?.message || error.message;
      showToast(errorMessage, "error");
    }
    return {
      isError: true,
      message: error.response?.data?.message || error.message,
    };
  }
};

export const postData = async (
  endpoint,
  body,
  customSuccessMessage = null,
  showToastOnSuccess = true
) => {
  try {
    const response = await apiClient.post(endpoint, body);

    const isSpecialEndpoint = Object.values(specialEndpoints).some((special) =>
      endpoint.includes(special)
    );
    
    const canShowToast = shouldShowToast(endpoint) && showToastOnSuccess && !isSpecialEndpoint;
    
    if (canShowToast) {
      const message =
        customSuccessMessage ||
        response.data?.message ||
        "Request completed successfully";
      showToast(message, "success");
    }
    return response.data;
  } catch (error) {
    // Don't show error toast for 401 (handled in interceptor)
    if (error.response?.status !== 401 && shouldShowToast(endpoint)) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred";

      const isSpecialEndpoint = Object.values(specialEndpoints).some((special) =>
        endpoint.includes(special)
      );
      if (!isSpecialEndpoint) {
        showToast(errorMessage, "error");
      }
    }
    return {
      isError: true,
      message: error.response?.data?.message || error.message,
    };
  }
};

export const putData = async (
  endpoint,
  body,
  customSuccessMessage = null,
  showToastOnSuccess = true
) => {
  try {
    const response = await apiClient.put(endpoint, body);
    
    // Only show success toast for endpoints that should show toasts
    if (shouldShowToast(endpoint) && showToastOnSuccess) {
      const message =
        customSuccessMessage ||
        response.data?.message ||
        "Update completed successfully";
      showToast(message, "success");
    }
    return response.data;
  } catch (error) {
    // Don't show error toast for 401 (handled in interceptor)
    if (error.response?.status !== 401 && shouldShowToast(endpoint)) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred";
      
      showToast(errorMessage, "error");
    }
    return {
      isError: true,
      message: error.response?.data?.message || error.message,
    };
  }
};

export const deleteData = async (
  endpoint,
  customSuccessMessage = null,
  showToastOnSuccess = true
) => {
  try {
    const response = await apiClient.delete(endpoint);
    
    // Only show success toast for endpoints that should show toasts
    if (shouldShowToast(endpoint) && showToastOnSuccess) {
      const message =
        customSuccessMessage ||
        response.data?.message ||
        "Deletion completed successfully";
      showToast(message, "success");
    }
    return response.data;
  } catch (error) {
    // Don't show error toast for 401 (handled in interceptor)
    if (error.response?.status !== 401 && shouldShowToast(endpoint)) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred";
      
      showToast(errorMessage, "error");
    }
    return {
      isError: true,
      message: error.response?.data?.message || error.message,
    };
  }
};

// =====================
// File Upload Helper
// =====================
export const uploadFile = async (endpoint, formData, customSuccessMessage = null, showToastOnSuccess = true) => {
  try {
    const response = await apiClient.post(endpoint, formData);
    
    if (shouldShowToast(endpoint) && showToastOnSuccess) {
      const message = customSuccessMessage || response.data?.message || 'File uploaded successfully';
      showToast(message, 'success');
    }
    return response.data;
  } catch (error) {
    if (error.response?.status !== 401 && shouldShowToast(endpoint)) {
      const errorMessage = error.response?.data?.message || error.message || 'Upload failed';
      showToast(errorMessage, 'error');
    }
    return {
      isError: true,
      message: error.response?.data?.message || error.message,
    };
  }
};

export default {
  getData,
  postData,
  putData,
  deleteData,
  uploadFile,
};